// Updated import path
import {
  VidyoConnector,
  VidyoConnectorOnModerationCommandReceived,
} from "@/features/vidyo-call/types/vidyoclient-nativewebrtc-sdk";
import {
  VidyoDeviceType,
  VidyoRoomModerationType,
} from "@/features/vidyo-call/types/vidyoclient-nativewebrtc-sdk.enums";

import { useVidyoStore } from "../../vidyo-call/utils/vidyo-store";

const isHandledDeviceType = (
  type: VidyoDeviceType,
): type is keyof typeof deviceHandlers => {
  return type in deviceHandlers;
};

const deviceHandlers = {
  [VidyoDeviceType.VIDYO_DEVICETYPE_LocalCamera]: {
    mute: () => useVidyoStore.getState().camera.mute(),
    setServerMute: (muted: boolean) =>
      useVidyoStore.getState().camera.setServerMute(muted),
  },
  [VidyoDeviceType.VIDYO_DEVICETYPE_LocalMicrophone]: {
    mute: () => useVidyoStore.getState().microphone.mute(),
    setServerMute: (muted: boolean) =>
      useVidyoStore.getState().microphone.setServerMute(muted),
  },
};

const handleMute: VidyoConnectorOnModerationCommandReceived = (
  deviceType,
  moderationType,
  shouldBeMuted,
) => {
  if (!isHandledDeviceType(deviceType)) {
    return;
  }

  const handler = deviceHandlers[deviceType];

  if (shouldBeMuted) {
    handler.mute();
  }
  if (
    moderationType === VidyoRoomModerationType.VIDYO_ROOMMODERATIONTYPE_HardMute
  ) {
    handler.setServerMute(shouldBeMuted);
  }
};

export async function createLocalDeviceLifecycle(
  vidyoConnector: VidyoConnector,
) {
  const addCamera = useVidyoStore.getState().camera.add;
  const removeCamera = useVidyoStore.getState().camera.remove;
  const selectCamera = useVidyoStore.getState().camera.select;
  const preferredCamera = useVidyoStore.getState().camera.preferred;

  const addMicrophone = useVidyoStore.getState().microphone.add;
  const removeMicrophone = useVidyoStore.getState().microphone.remove;
  const selectMicrophone = useVidyoStore.getState().microphone.select;
  const preferredMicrophone = useVidyoStore.getState().microphone.preferred;

  const addSpeaker = useVidyoStore.getState().speaker.add;
  const removeSpeaker = useVidyoStore.getState().speaker.remove;
  const selectSpeaker = useVidyoStore.getState().speaker.select;
  const preferredSpeaker = useVidyoStore.getState().speaker.preferred;

  await Promise.all([
    vidyoConnector.SetCameraPrivacy({ privacy: true }),
    vidyoConnector.SetMicrophonePrivacy({ privacy: true }),
    vidyoConnector.SetSpeakerPrivacy({ privacy: true }),
    vidyoConnector.RegisterLocalCameraEventListener({
      onAdded: async (localCamera) => {
        if (localCamera.id === preferredCamera) {
          // without setTimeout, the device is not going be selected properly,
          // because its going to be overwritten. I dont know why, its copied from cv1
          window.setTimeout(() => {
            vidyoConnector.SelectLocalCamera({
              localCamera: localCamera,
            });
          }, 50);
        }

        if (!preferredCamera && localCamera.name.includes("Default")) {
          window.setTimeout(() => {
            vidyoConnector.SelectLocalCamera({
              localCamera: localCamera,
            });
          }, 50);
        }
        addCamera(localCamera);
      },
      onRemoved: removeCamera,
      onSelected: selectCamera,

      onStateUpdated: () => {
        // console.log("onLocalCameraStateUpdated", localCamera);
      },
    }),

    vidyoConnector.RegisterLocalMicrophoneEventListener({
      onAdded: async (localMicrophone) => {
        if (localMicrophone.id === preferredMicrophone) {
          window.setTimeout(() => {
            vidyoConnector.SelectLocalMicrophone({
              localMicrophone: localMicrophone,
            });
          }, 50);
        }

        if (!preferredMicrophone && localMicrophone.name.includes("Default")) {
          window.setTimeout(() => {
            vidyoConnector.SelectLocalMicrophone({
              localMicrophone: localMicrophone,
            });
          }, 50);
        }
        addMicrophone(localMicrophone);
      },
      onRemoved: removeMicrophone,
      onSelected: selectMicrophone,
      onStateUpdated: () => {
        // console.log("onLocalMicrophoneStateUpdated", localMicrophone);
      },
    }),

    vidyoConnector.RegisterLocalSpeakerEventListener({
      onAdded: async (localSpeaker) => {
        if (localSpeaker.id === preferredSpeaker) {
          window.setTimeout(() => {
            vidyoConnector.SelectLocalSpeaker({
              localSpeaker: localSpeaker,
            });
          }, 50);
        }

        if (!preferredSpeaker && localSpeaker.name.includes("Default")) {
          window.setTimeout(() => {
            vidyoConnector.SelectLocalSpeaker({
              localSpeaker: localSpeaker,
            });
          }, 50);
        }
        addSpeaker(localSpeaker);
      },
      onRemoved: removeSpeaker,
      onSelected: selectSpeaker,
      onStateUpdated: () => {
        // console.log("onLocalSpeakerStateUpdated", localSpeaker);
      },
    }),
    vidyoConnector.RegisterModerationCommandEventListener({
      onModerationCommandReceived: handleMute,
    }),
  ]);
}
